import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNotification } from '../../../../contexts/NotificationContext';
import { DRAFT_KEYS, loadDraft, hasDraft, clearDraft, saveDraft } from '../../../../utils/draftUtils';
import { 
  fetchPatientData, 
  fetchAldreteScore, 
  saveAldreteScore, 
  updateAldreteScore,
  generateAldreteShareToken
} from '../api/aldreteApi';
import { mapBackendToForm, mapFormToBackend } from '../utils/mapping';
import { useAldreteValidation } from './useAldreteValidation';
import { categories, timeIntervals, timeValues } from '../constants/options';

export const useAldreteScore = (uhid_no, navigate) => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();
  
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ open: false, message: "", severity: "success" });

  const [formData, setFormData] = useState({
    patient_name: "",
    age: "",
    gender: "",
    uhid_no: uhid_no || "",
    surgery_procedure: "",
    anaesthesia: "",
    totalScores: Array(timeIntervals.length).fill(0),
    scores: categories.map(() => timeIntervals.map(() => 0)),
    selectedOptions: Array(categories.length * timeIntervals.length).fill(""),
    checkboxStates: categories.map(() => timeIntervals.map(() => false)),
    timeValues: categories.map(() => timeIntervals.map(() => "")),
    baseTime: Array(categories.length).fill(""),
    admTime: Array(categories.length).fill(""),
    dischargeTime: Array(categories.length).fill(""),
  });

  const { errors, setErrors, validateField, validateForm } = useAldreteValidation(formData);

  // Queries
  const { data: patientInfo } = useQuery({
    queryKey: ['patient', uhid_no],
    queryFn: () => fetchPatientData(uhid_no),
    enabled: !!uhid_no
  });

  const { data: existingData } = useQuery({
    queryKey: ['aldreteScore', uhid_no],
    queryFn: () => fetchAldreteScore(uhid_no),
    enabled: !!uhid_no,
    retry: false
  });

  useEffect(() => {
    if (existingData) {
      setSuccess(true);
      setFormData(prev => ({ ...prev, ...mapBackendToForm(existingData, uhid_no) }));
    }
  }, [existingData, uhid_no]);

  // Effects
  useEffect(() => {
    if (patientInfo) {
      setFormData(prev => ({ 
        ...prev, 
        patient_name: patientInfo.name,
        age: patientInfo.age || '',
        gender: patientInfo.sex || '',
      }));
    }
  }, [patientInfo]);

  // Draft loading and saving
  useEffect(() => {
    if (uhid_no && hasDraft(DRAFT_KEYS.POST_OPERATIVE_ANALGESIA_ASSESSMENT, uhid_no)) {
      const saved = loadDraft(DRAFT_KEYS.POST_OPERATIVE_ANALGESIA_ASSESSMENT, uhid_no);
      if (saved && saved.formData) {
        setFormData(prev => ({ ...prev, ...saved.formData }));
      }
    }
  }, [uhid_no]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (uhid_no) {
        saveDraft(DRAFT_KEYS.POST_OPERATIVE_ANALGESIA_ASSESSMENT, uhid_no, formData);
      }
    }, 2000);
    return () => clearTimeout(timeoutId);
  }, [formData, uhid_no]);

  // Logic Helpers
  const addMinutesToTime = useCallback((timeStr, minutesToAdd) => {
    if (typeof timeStr !== "string" || !timeStr.includes(":")) return "";
    const [hours, minutes] = timeStr.split(":").map(Number);
    const totalMinutes = hours * 60 + minutes + minutesToAdd;
    const newHours = Math.floor(totalMinutes / 60) % 24;
    const newMinutes = totalMinutes % 60;
    return `${newHours.toString().padStart(2, "0")}:${newMinutes.toString().padStart(2, "0")}`;
  }, []);

  const calculateTotalScores = useCallback((scores) => {
    return timeIntervals.map((_, timeIndex) => {
      return categories.reduce((total, _, catIndex) => {
        if (scores[catIndex] && scores[catIndex][timeIndex] !== undefined) {
          return total + (scores[catIndex][timeIndex] || 0);
        }
        return total;
      }, 0);
    });
  }, []);

  // Handlers
  const handleInputChange = useCallback((event) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  }, [errors, setErrors]);

  const handleCheckboxClick = useCallback((categoryIndex, timeIndex) => {
    setFormData(prev => {
      const newCheckboxStates = [...prev.checkboxStates];
      const newTimeValues = [...prev.timeValues];
      
      newCheckboxStates[categoryIndex][timeIndex] = !newCheckboxStates[categoryIndex][timeIndex];
      
      if (newCheckboxStates[categoryIndex][timeIndex] && prev.baseTime[categoryIndex]) {
        const intervalStr = timeIntervals[timeIndex];
        const minutesToAdd = timeValues[intervalStr] || 0;
        if (minutesToAdd > 0) {
          newTimeValues[categoryIndex][timeIndex] = addMinutesToTime(prev.baseTime[categoryIndex], minutesToAdd);
        }
      } else {
        newTimeValues[categoryIndex][timeIndex] = "";
      }
      
      return {
        ...prev,
        checkboxStates: newCheckboxStates,
        timeValues: newTimeValues
      };
    });
  }, [addMinutesToTime]);

  const handleScoreChange = useCallback((catIndex, timeIndex, event) => {
    const newScore = parseInt(event.target.value, 10);
    const selectedOption = categories[catIndex].options[newScore];
    
    setFormData(prev => {
      const newScores = [...prev.scores];
      newScores[catIndex][timeIndex] = newScore;
      
      const newSelectedOptions = [...prev.selectedOptions];
      newSelectedOptions[catIndex * timeIntervals.length + timeIndex] = selectedOption;
      
      const newTotalScores = calculateTotalScores(newScores);
      
      return {
        ...prev,
        scores: newScores,
        selectedOptions: newSelectedOptions,
        totalScores: newTotalScores
      };
    });
  }, [calculateTotalScores]);

  const handleBaseTimeChange = useCallback((catIndex, value) => {
    setFormData(prev => {
      const newBaseTimes = [...prev.baseTime];
      newBaseTimes[catIndex] = value;
      return { ...prev, baseTime: newBaseTimes };
    });
  }, []);

  const handleAdmTimeChange = useCallback((catIndex, value) => {
    setFormData(prev => {
      const newAdmTimes = [...prev.admTime];
      newAdmTimes[catIndex] = value;
      return { ...prev, admTime: newAdmTimes };
    });
  }, []);

  const handleDischargeTimeChange = useCallback((catIndex, value) => {
    setFormData(prev => {
      const newDischargeTimes = [...prev.dischargeTime];
      newDischargeTimes[catIndex] = value;
      return { ...prev, dischargeTime: newDischargeTimes };
    });
  }, []);

  const handleBlur = useCallback((event) => {
    const { name, value } = event.target;
    validateField(name, value);
  }, [validateField]);

  // Mutations
  const mutation = useMutation({
    mutationFn: (data) => {
      if (success) {
        return updateAldreteScore(uhid_no, data);
      } else {
        return saveAldreteScore(data);
      }
    },
    onSuccess: () => {
      showNotification('success', success ? 'Aldrete Score Form updated successfully!' : 'Aldrete Score Form submitted successfully!');
      queryClient.invalidateQueries(['aldreteScore', uhid_no]);
      setSuccess(true);
      clearDraft(DRAFT_KEYS.POST_OPERATIVE_ANALGESIA_ASSESSMENT, uhid_no);
      setTimeout(() => {
        navigate(`/mainPage/${uhid_no}`, {
          state: { from: 'PostOperativePhase', status: 'success', step: 3 },
        });
      }, 1000);
    },
    onError: (error) => {
      console.error('Submission error:', error);
      showNotification('error', 'Error in submitting Aldrete Score form!');
    }
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!validateForm()) return;
    mutation.mutate(mapFormToBackend(formData));
  };

  const handleShareClick = async () => {
    try {
      const res = await generateAldreteShareToken(uhid_no);
      const token = res.token;
      // Note: The original link was to intraOperativeNursingRecord, which might be a copy-paste error in original code.
      // I'll keep it as is if it's correct, but usually it should point to current form.
      // Original code line 592: const link = `${API_BASE_URL}/intraOperativeNursingRecord/${uhid_no}?token=${token}`;
      const link = `${window.location.origin}/AldreteScore/${uhid_no}?token=${token}`;
      const message = `Dear Doctor,\n\nPlease fill the post-operative analgesia chart:\n\n${link}`;
      await navigator.clipboard.writeText(link);
      setSubmitStatus({ open: true, message: "Secure link copied to clipboard!", severity: "info" });
      const phoneNumber = '+916379605575';
      window.open(`http://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
    } catch (error) {
      console.error("Error generating share token:", error);
      setSubmitStatus({ open: true, message: "Failed to generate shareable link", severity: "error" });
    }
  };

  return {
    formData, setFormData,
    errors, setErrors,
    success, setSuccess,
    loading: patientInfo === undefined && existingData === undefined,
    submitStatus, setSubmitStatus,
    handleInputChange, handleCheckboxClick, handleScoreChange, 
    handleBaseTimeChange, handleAdmTimeChange, handleDischargeTimeChange, handleBlur,
    handleSubmit, handleShareClick,
    mutationLoading: mutation.isLoading
  };
};
